import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * Hand-made SVG illustrations for the self-registration screens and the
 * approval queue (empty, success, error and link states). Theme-aware: the
 * neutral surfaces use `mym-ill-*` classes (registration.css) that switch
 * colours in dark mode; accent colours stay vivid in both themes.
 * Decorative only — the surrounding copy carries the meaning.
 */

const COLORS = {
  blue: '#2c7be5',
  blueDark: '#1a5bb5',
  green: '#22b35e',
  orange: '#f5803e',
  amber: '#f5a23e',
  yellow: '#ffc545',
  pink: '#ff679b',
  red: '#e63757',
  purple: '#7d6ee7'
};

const Sparkle = ({ x, y, size = 1, color = COLORS.yellow, opacity = 1 }) => (
  <path
    transform={`translate(${x} ${y}) scale(${size})`}
    d="M0-8C1.2-1.2 1.2-1.2 8 0 1.2 1.2 1.2 1.2 0 8-1.2 1.2-1.2 1.2-8 0-1.2-1.2-1.2-1.2 0-8Z"
    fill={color}
    opacity={opacity}
  />
);

Sparkle.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  opacity: PropTypes.number
};

const CheckBadge = ({ cx, cy, r = 19, color = COLORS.green }) => (
  <g>
    <circle cx={cx} cy={cy} r={r + 3} className="mym-ill-ring" />
    <circle cx={cx} cy={cy} r={r} fill={color} />
    <path
      d={`M${cx - r * 0.45} ${cy + r * 0.02}l${r * 0.32} ${r * 0.32} ${
        r * 0.58
      } -${r * 0.62}`}
      fill="none"
      stroke="#fff"
      strokeWidth={r * 0.21}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </g>
);

CheckBadge.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  r: PropTypes.number,
  color: PropTypes.string
};

const Svg = ({ viewBox, width, className, children }) => (
  <svg
    viewBox={viewBox}
    width={width}
    className={classNames('mym-ill', className)}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

Svg.propTypes = {
  viewBox: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  children: PropTypes.node
};

const illustrationPropTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string
};

/* ------------------------------------------------------------------------ */

/** Request submitted: letter tucked into an envelope + paper plane. */
export const SubmittedIllustration = ({ width = 200, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="112" cy="94" rx="86" ry="66" className="mym-ill-soft" />
    <path
      d="M24 70c14-28 42-40 66-26"
      fill="none"
      className="mym-ill-trail"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeDasharray="1 7"
    />
    <g transform="translate(8 64) rotate(-18)">
      <path d="M0 12 32 0 22 28 15 18Z" fill={COLORS.blue} />
      <path d="M15 18 32 0 11 14Z" fill={COLORS.blueDark} />
    </g>
    <rect
      x="58"
      y="72"
      width="104"
      height="64"
      rx="9"
      className="mym-ill-navy-soft"
    />
    <rect
      x="70"
      y="40"
      width="80"
      height="68"
      rx="6"
      className="mym-ill-paper"
    />
    <rect x="80" y="52" width="36" height="6" rx="3" fill={COLORS.blue} />
    <rect
      x="80"
      y="64"
      width="60"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="80"
      y="74"
      width="52"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="80"
      y="84"
      width="44"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <path
      d="M58 82 110 110 162 82v45a9 9 0 0 1-9 9H67a9 9 0 0 1-9-9Z"
      className="mym-ill-paper mym-ill-outline"
      strokeWidth="1.5"
    />
    <path
      d="M60 132 96 106M160 132 124 106"
      fill="none"
      className="mym-ill-fold"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <CheckBadge cx={160} cy={128} r={18} />
    <Sparkle x={182} y={50} />
    <Sparkle x={42} y={124} size={0.65} color={COLORS.pink} />
    <circle cx="194" cy="96" r="4" fill={COLORS.blue} opacity=".45" />
    <circle cx="38" cy="30" r="3" fill={COLORS.green} opacity=".6" />
  </Svg>
);
SubmittedIllustration.propTypes = illustrationPropTypes;

/** Self-registration closed: clipboard with a padlock. */
export const RegistrationClosedIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="92" rx="86" ry="66" className="mym-ill-soft-orange" />
    <rect
      x="58"
      y="30"
      width="88"
      height="114"
      rx="11"
      className="mym-ill-navy"
    />
    <rect
      x="66"
      y="42"
      width="72"
      height="94"
      rx="6"
      className="mym-ill-paper"
    />
    <rect x="84" y="23" width="36" height="15" rx="6" fill={COLORS.orange} />
    <circle cx="102" cy="30" r="3" className="mym-ill-paper" />
    <rect
      x="76"
      y="56"
      width="34"
      height="6"
      rx="3"
      fill={COLORS.orange}
      opacity=".85"
    />
    <rect
      x="76"
      y="70"
      width="52"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="76"
      y="81"
      width="44"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="76"
      y="92"
      width="48"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="76"
      y="103"
      width="30"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <path
      d="M142 104V94a16 16 0 0 1 32 0v10"
      fill="none"
      className="mym-ill-stroke"
      strokeWidth="7"
      strokeLinecap="round"
    />
    <rect
      x="130"
      y="100"
      width="56"
      height="44"
      rx="10"
      className="mym-ill-ring-fill"
    />
    <rect x="133" y="103" width="50" height="38" rx="8" fill={COLORS.orange} />
    <circle cx="158" cy="118" r="5.5" fill="#fff" />
    <rect x="155.5" y="120" width="5" height="11" rx="2.5" fill="#fff" />
    <Sparkle x={184} y={46} color={COLORS.yellow} />
    <Sparkle x={40} y={126} size={0.6} color={COLORS.blue} opacity={0.7} />
    <circle cx="36" cy="58" r="3.5" fill={COLORS.orange} opacity=".5" />
  </Svg>
);
RegistrationClosedIllustration.propTypes = illustrationPropTypes;

/** Invite link expired: hourglass with a clock badge. */
export const LinkExpiredIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="92" rx="86" ry="66" className="mym-ill-soft-orange" />
    <path
      d="M84 38h52c0 26-19 33-19 47s19 21 19 47H84c0-26 19-33 19-47S84 64 84 38Z"
      className="mym-ill-paper mym-ill-outline"
      strokeWidth="2.5"
    />
    <path
      d="M93 50h34c-3 9-11 15-17 21-6-6-14-12-17-21Z"
      fill={COLORS.yellow}
    />
    <rect
      x="108.5"
      y="72"
      width="3"
      height="42"
      rx="1.5"
      fill={COLORS.yellow}
    />
    <path d="M89 132c3-13 12-19 21-19s18 6 21 19Z" fill={COLORS.amber} />
    <rect
      x="72"
      y="26"
      width="76"
      height="13"
      rx="6.5"
      className="mym-ill-navy"
    />
    <rect
      x="72"
      y="131"
      width="76"
      height="13"
      rx="6.5"
      className="mym-ill-navy"
    />
    <circle cx="162" cy="120" r="23" className="mym-ill-ring-fill" />
    <circle cx="162" cy="120" r="20" fill={COLORS.red} />
    <path
      d="M162 109v11l7 5"
      fill="none"
      stroke="#fff"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Sparkle x={52} y={46} color={COLORS.yellow} />
    <circle cx="186" cy="54" r="3.5" fill={COLORS.orange} opacity=".55" />
    <circle cx="44" cy="120" r="3" fill={COLORS.blue} opacity=".5" />
  </Svg>
);
LinkExpiredIllustration.propTypes = illustrationPropTypes;

/** Invite link invalid: a broken chain link. */
export const LinkInvalidIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="90" rx="86" ry="66" className="mym-ill-soft" />
    <g transform="rotate(-32 110 88)">
      <rect
        x="34"
        y="70"
        width="66"
        height="36"
        rx="18"
        fill="none"
        stroke={COLORS.blue}
        strokeWidth="11"
      />
      <rect
        x="120"
        y="70"
        width="66"
        height="36"
        rx="18"
        fill="none"
        className="mym-ill-stroke"
        strokeWidth="11"
      />
    </g>
    <path
      d="M100 60 94 47M112 57l3-14M123 64l11-7"
      fill="none"
      stroke={COLORS.red}
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M97 118l-10 7M110 122l-2 13M122 116l8 9"
      fill="none"
      stroke={COLORS.orange}
      strokeWidth="4"
      strokeLinecap="round"
    />
    <Sparkle x={184} y={44} color={COLORS.yellow} />
    <circle cx="38" cy="50" r="3.5" fill={COLORS.pink} opacity=".6" />
  </Svg>
);
LinkInvalidIllustration.propTypes = illustrationPropTypes;

/** Invite link already used: a key with a check badge. */
export const LinkUsedIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="90" rx="86" ry="66" className="mym-ill-soft-green" />
    <g transform="rotate(-28 110 90)">
      <circle cx="68" cy="90" r="27" fill={COLORS.yellow} />
      <circle cx="68" cy="90" r="10" className="mym-ill-paper" />
      <rect x="90" y="83" width="84" height="14" rx="7" fill={COLORS.yellow} />
      <rect x="146" y="94" width="10" height="17" rx="3" fill={COLORS.yellow} />
      <rect x="161" y="94" width="10" height="12" rx="3" fill={COLORS.yellow} />
    </g>
    <CheckBadge cx={160} cy={122} r={19} />
    <Sparkle x={48} y={44} color={COLORS.green} size={0.8} />
    <circle cx="188" cy="58" r="4" fill={COLORS.blue} opacity=".45" />
  </Svg>
);
LinkUsedIllustration.propTypes = illustrationPropTypes;

/**
 * Empty queue: an inbox tray. `mark="check"` (all caught up) shows a check
 * bubble; `mark="dots"` (nothing yet) shows an ellipsis bubble.
 */
export const QueueEmptyIllustration = ({
  width = 190,
  className,
  mark = 'check',
  accent = COLORS.green
}) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse
      cx="110"
      cy="96"
      rx="86"
      ry="62"
      className={mark === 'check' ? 'mym-ill-soft-green' : 'mym-ill-soft'}
    />
    <path
      d="M50 104 70 62h80l20 42"
      className="mym-ill-paper mym-ill-outline"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <rect
      x="80"
      y="74"
      width="60"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <rect
      x="76"
      y="86"
      width="68"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <path
      d="M50 104h36a24 24 0 0 0 48 0h36v26a10 10 0 0 1-10 10H60a10 10 0 0 1-10-10Z"
      fill={COLORS.blue}
    />
    <path
      d="M50 104h36a24 24 0 0 0 48 0h36"
      fill="none"
      stroke={COLORS.blueDark}
      strokeWidth="3"
      strokeLinejoin="round"
    />
    {mark === 'check' ? (
      <CheckBadge cx={110} cy={40} r={21} color={accent} />
    ) : (
      <g>
        <circle cx="110" cy="40" r="24" className="mym-ill-ring-fill" />
        <circle cx="110" cy="40" r="21" fill={accent} />
        <circle cx="100" cy="40" r="3.5" fill="#fff" />
        <circle cx="110" cy="40" r="3.5" fill="#fff" />
        <circle cx="120" cy="40" r="3.5" fill="#fff" />
      </g>
    )}
    <Sparkle x={160} y={34} color={COLORS.yellow} size={0.9} />
    <Sparkle x={58} y={46} color={COLORS.pink} size={0.6} />
    <circle cx="186" cy="84" r="3.5" fill={COLORS.purple} opacity=".5" />
  </Svg>
);
QueueEmptyIllustration.propTypes = {
  ...illustrationPropTypes,
  mark: PropTypes.oneOf(['check', 'dots']),
  accent: PropTypes.string
};

/** Could not load: a cloud with a lightning bolt. */
export const QueueErrorIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="92" rx="86" ry="62" className="mym-ill-soft-orange" />
    <ellipse cx="112" cy="134" rx="50" ry="6" className="mym-ill-shadow" />
    <g className="mym-ill-paper">
      <circle cx="82" cy="92" r="24" />
      <circle cx="112" cy="74" r="32" />
      <circle cx="144" cy="94" r="22" />
      <rect x="82" y="92" width="62" height="24" />
    </g>
    <path
      d="M116 64 96 96h15l-8 30 25-38h-15l9-24Z"
      fill={COLORS.yellow}
      stroke={COLORS.orange}
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <Sparkle x={178} y={48} color={COLORS.orange} size={0.8} />
    <circle cx="40" cy="60" r="3.5" fill={COLORS.blue} opacity=".45" />
  </Svg>
);
QueueErrorIllustration.propTypes = illustrationPropTypes;

/* ------------------------------------------------------------------------ */
/*               Heroes for the gradient side panel (always dark)            */
/* ------------------------------------------------------------------------ */

const CheckRow = ({ y, done = true, width = 68 }) => (
  <g>
    <circle cx="112" cy={y} r="7" fill={done ? COLORS.green : COLORS.yellow} />
    {done ? (
      <path
        d={`M108.5 ${y}l2.5 2.5 4.5-5`}
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <circle cx="112" cy={y} r="2" fill="#fff" />
    )}
    <rect
      x="126"
      y={y - 3.5}
      width={width}
      height="7"
      rx="3.5"
      fill="#d6e2f3"
    />
  </g>
);

CheckRow.propTypes = {
  y: PropTypes.number.isRequired,
  done: PropTypes.bool,
  width: PropTypes.number
};

/** Registration hero: an access-request card surrounded by care icons. */
export const RegisterHeroIllustration = ({ width = 300, className }) => (
  <Svg viewBox="0 0 300 230" width={width} className={className}>
    <circle cx="150" cy="118" r="104" fill="#fff" opacity=".06" />
    <circle cx="150" cy="118" r="76" fill="#fff" opacity=".07" />
    <rect
      x="94"
      y="44"
      width="116"
      height="152"
      rx="14"
      fill="#0b2c55"
      opacity=".35"
    />
    <rect x="90" y="38" width="116" height="152" rx="14" fill="#fff" />
    <rect x="124" y="28" width="48" height="20" rx="8" fill={COLORS.green} />
    <circle cx="148" cy="38" r="4" fill="#fff" />
    <circle cx="148" cy="90" r="23" fill="#e3edfb" />
    <circle cx="148" cy="84" r="8.5" fill="#154d86" />
    <path d="M133 103a15 13 0 0 1 30 0Z" fill="#154d86" />
    <CheckRow y={132} />
    <CheckRow y={152} width={56} />
    <CheckRow y={172} done={false} width={62} />
    <g transform="translate(40 84) rotate(-35)">
      <rect width="46" height="19" rx="9.5" fill="#fff" />
      <path d="M23 0h13.5a9.5 9.5 0 0 1 0 19H23Z" fill={COLORS.yellow} />
    </g>
    <g transform="translate(240 70)">
      <circle r="19" fill={COLORS.green} />
      <path
        d="M-7 0h14M0-7v14"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </g>
    <path
      transform="translate(236 152)"
      d="M0 6c-6-8-18-4-16 5 2 7 16 15 16 15s14-8 16-15c2-9-10-13-16-5Z"
      fill={COLORS.pink}
    />
    <g transform="translate(58 168)">
      <circle r="15" fill={COLORS.blue} />
      <path
        d="M-6 1l4 4 8-9"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <Sparkle x={70} y={40} color={COLORS.yellow} />
    <Sparkle x={262} y={112} color="#fff" size={0.6} opacity={0.8} />
    <Sparkle x={34} y={128} color="#fff" size={0.5} opacity={0.7} />
    <circle cx="224" cy="30" r="4" fill="#fff" opacity=".5" />
  </Svg>
);
RegisterHeroIllustration.propTypes = illustrationPropTypes;

/** Set-password hero: a shield with a keyhole and a check. */
export const SetPasswordHeroIllustration = ({ width = 300, className }) => (
  <Svg viewBox="0 0 300 230" width={width} className={className}>
    <circle cx="150" cy="116" r="104" fill="#fff" opacity=".06" />
    <circle cx="150" cy="116" r="76" fill="#fff" opacity=".07" />
    <path
      d="M154 40l64 23v47c0 44-29 72-64 87-35-15-64-43-64-87V63Z"
      fill="#0b2c55"
      opacity=".35"
    />
    <path
      d="M150 34l64 23v47c0 44-29 72-64 87-35-15-64-43-64-87V57Z"
      fill="#fff"
    />
    <path
      d="M150 51l49 17v36c0 34-22 56-49 68-27-12-49-34-49-68V68Z"
      fill="#e3edfb"
    />
    <circle cx="150" cy="100" r="15" fill="#154d86" />
    <path d="M143 108h14l4 28h-22Z" fill="#154d86" />
    <g transform="translate(214 162)">
      <circle r="23" fill="#0b2c55" opacity=".35" />
      <circle r="20" fill={COLORS.green} />
      <path
        d="M-9 0l6 6 12-13"
        fill="none"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <g transform="translate(64 150) rotate(-40)">
      <circle r="14" fill={COLORS.yellow} />
      <circle r="5" fill="#154d86" opacity=".6" />
      <rect x="12" y="-4" width="40" height="8" rx="4" fill={COLORS.yellow} />
      <rect x="38" y="2" width="6" height="10" rx="2" fill={COLORS.yellow} />
      <rect x="46" y="2" width="6" height="7" rx="2" fill={COLORS.yellow} />
    </g>
    <Sparkle x={236} y={52} color={COLORS.yellow} />
    <Sparkle x={62} y={60} color="#fff" size={0.6} opacity={0.8} />
    <circle cx="248" cy="112" r="4" fill="#fff" opacity=".5" />
  </Svg>
);
SetPasswordHeroIllustration.propTypes = illustrationPropTypes;

/** Email domains: an envelope with an @ badge, linked to an organization. */
export const EmailDomainsIllustration = ({ width = 190, className }) => (
  <Svg viewBox="0 0 220 170" width={width} className={className}>
    <ellipse cx="110" cy="92" rx="86" ry="62" className="mym-ill-soft" />
    <ellipse cx="110" cy="140" rx="56" ry="6" className="mym-ill-shadow" />
    <path
      d="M52 116c-14-6-20-20-14-34"
      fill="none"
      className="mym-ill-trail"
      strokeWidth="2.5"
      strokeDasharray="4 6"
      strokeLinecap="round"
    />
    <rect
      x="64"
      y="62"
      width="100"
      height="68"
      rx="10"
      className="mym-ill-paper mym-ill-outline"
      strokeWidth="2.5"
    />
    <path
      d="M66 66l48 36 48-36"
      fill="none"
      className="mym-ill-fold"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="80"
      y="110"
      width="34"
      height="5"
      rx="2.5"
      className="mym-ill-line"
    />
    <g>
      <circle cx="160" cy="62" r="25" className="mym-ill-ring" />
      <circle cx="160" cy="62" r="22" fill={COLORS.blue} />
      <text
        x="160"
        y="63"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Poppins, Arial, sans-serif"
        fontSize="26"
        fontWeight="700"
        fill="#fff"
      >
        @
      </text>
    </g>
    <g>
      <circle cx="50" cy="118" r="21" className="mym-ill-ring" />
      <circle cx="50" cy="118" r="18" fill={COLORS.green} />
      <rect x="41" y="108" width="18" height="20" rx="2" fill="#fff" />
      <rect x="45" y="112" width="4" height="4" rx="1" fill={COLORS.green} />
      <rect x="51" y="112" width="4" height="4" rx="1" fill={COLORS.green} />
      <rect x="45" y="118" width="4" height="4" rx="1" fill={COLORS.green} />
      <rect x="51" y="118" width="4" height="4" rx="1" fill={COLORS.green} />
    </g>
    <Sparkle x={190} y={104} size={0.8} />
    <Sparkle x={34} y={64} size={0.6} color={COLORS.pink} />
    <circle cx="186" cy="34" r="3.5" fill={COLORS.purple} opacity=".6" />
  </Svg>
);
EmailDomainsIllustration.propTypes = illustrationPropTypes;

/* ------------------------------------------------------------------------ */

const MEDPASS_TIMES = ['8 AM', '12 PM', '4 PM', '8 PM'];
const MEDPASS_COLUMN_X = [128, 160, 192, 224];
const MEDPASS_ROW_Y = [88, 122, 156];
// Each row: given | due now | later, per med pass time.
const MEDPASS_ROWS = [
  ['given', 'given', 'later', 'later'],
  ['given', 'given', 'later', 'later'],
  ['given', 'due', 'later', 'later']
];

const MedpassCell = ({ cx, cy, state }) => {
  if (state === 'given') {
    return (
      <g>
        <circle cx={cx} cy={cy} r="8.5" fill={COLORS.green} />
        <path
          d={`M${cx - 3.8} ${cy + 0.2}l2.7 2.7 5-5.4`}
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  }
  if (state === 'due') {
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r="8.5"
          fill="#fff"
          stroke={COLORS.blue}
          strokeWidth="2"
        />
        <circle cx={cx} cy={cy} r="2.6" fill={COLORS.blue} />
      </g>
    );
  }
  return (
    <circle
      cx={cx}
      cy={cy}
      r="8"
      fill="none"
      stroke="#d6e2f3"
      strokeWidth="1.5"
    />
  );
};

MedpassCell.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  state: PropTypes.oneOf(['given', 'due', 'later']).isRequired
};

/**
 * Sign-in hero: today's medpass, the way staff see it — three medications
 * across the day's pass times, doses given so far, the next one due now.
 */
export const MedpassHeroIllustration = ({ width = 300, className }) => (
  <Svg viewBox="0 0 300 230" width={width} className={className}>
    <circle cx="150" cy="116" r="104" fill="#fff" opacity=".06" />
    <rect
      x="46"
      y="40"
      width="216"
      height="164"
      rx="14"
      fill="#0b2c55"
      opacity=".35"
    />
    <rect x="40" y="32" width="216" height="164" rx="14" fill="#fff" />
    {/* The pass happening now */}
    <rect x="145" y="44" width="30" height="132" rx="8" fill="#e8f0fb" />
    <text
      x="56"
      y="60"
      fontSize="11"
      fontWeight="700"
      fill="#154d86"
      fontFamily="inherit"
    >
      Today
    </text>
    {MEDPASS_TIMES.map((time, index) => (
      <text
        key={time}
        x={MEDPASS_COLUMN_X[index]}
        y="60"
        fontSize="8.5"
        fontWeight="600"
        textAnchor="middle"
        fill={index === 1 ? '#154d86' : '#748194'}
        fontFamily="inherit"
      >
        {time}
      </text>
    ))}
    {MEDPASS_ROW_Y.map((y, row) => (
      <g key={y}>
        {row > 0 && (
          <path
            d={`M54 ${y - 17}H242`}
            stroke="#eef3fa"
            strokeWidth="1"
            fill="none"
          />
        )}
        <g transform={`translate(56 ${y - 5})`}>
          <rect width="20" height="10" rx="5" fill="#d6e2f3" />
          <path
            d="M10 0h5a5 5 0 0 1 0 10h-5Z"
            fill={[COLORS.blue, COLORS.amber, COLORS.purple][row]}
          />
        </g>
        <rect
          x="82"
          y={y - 3.5}
          width={[30, 24, 28][row]}
          height="7"
          rx="3.5"
          fill="#d6e2f3"
        />
        {MEDPASS_COLUMN_X.map((x, column) => (
          <MedpassCell
            key={x}
            cx={x}
            cy={y}
            state={MEDPASS_ROWS[row][column]}
          />
        ))}
      </g>
    ))}
    {/* Doses given so far today: 7 of 12 */}
    <rect x="56" y="182" width="188" height="5" rx="2.5" fill="#eef3fa" />
    <rect x="56" y="182" width="110" height="5" rx="2.5" fill={COLORS.green} />
  </Svg>
);
MedpassHeroIllustration.propTypes = illustrationPropTypes;
